import { useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchUserPermissions } from "@/api/permissions";
import { queryKeys } from "@/api/queryKeys";
import { useApiToken } from "@/auth/useApiToken";
import { features } from "@/config";
import { AllowAllPermissionService, type IPermissionService, PermissionService } from "@/services/PermissionService";

const PermissionContext = createContext<IPermissionService | null>(null);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const getApiToken = useApiToken();
  const { t } = useTranslation("app");
  const email = accounts[0]?.username;

  const { data, isError, error } = useQuery({
    queryKey: queryKeys.userPermissions(email ?? ""),
    queryFn: async () => {
      if (!email) throw new Error("Cannot fetch permissions without a signed-in user");
      const token = await getApiToken();
      return fetchUserPermissions(token, email);
    },
    enabled: features.permissions && !!email,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!isError) return;
    console.error("Failed to load user permissions", error);
    const id = toast.error(t("permissions.loadFailed"), {
      id: "permissions-load-failed",
      duration: Number.POSITIVE_INFINITY,
    });
    return () => {
      toast.dismiss(id);
    };
  }, [isError, error, t]);

  const service = useMemo<IPermissionService>(() => {
    if (!features.permissions) return new AllowAllPermissionService();
    if (!email) return new PermissionService(false, new Map(), true);
    if (!data) return new PermissionService(false, new Map(), false);
    return new PermissionService(
      data.isUserAdmin,
      new Map(data.laboratoryPermissions.map((l) => [l.laboratoryId, new Set(l.permissions)])),
      true,
    );
  }, [email, data]);

  return <PermissionContext.Provider value={service}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): IPermissionService => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error("usePermissions must be used within PermissionProvider");
  return ctx;
};
